<template>
  <v-card align="center" justify="center" min-width="21vw" outlined>
    <v-card-title>
      <v-list-item>
        <v-list-item-title><h2>{{ $t('title_ImportMembers') }}</h2></v-list-item-title>
        <v-spacer/>
        <v-list-item-action>
          <v-icon class="icon" icon @click="close">fa-times</v-icon>
        </v-list-item-action>
      </v-list-item>
    </v-card-title>
    <v-card-text class="px-7 pt-7">
      <v-overlay :dark="false" :absolute="true" :value="overlayImporting"><v-progress-circular :size="70" width="5" indeterminate/></v-overlay>
      <v-stepper v-model="currentStep" :alt-labels="true" class="elevation-0">
        <v-stepper-header class="elevation-0">
          <v-stepper-step :complete=" currentStep > 1" step="1">{{ $t('steps_ImportMembers.upload') }}</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete=" currentStep > 2" step="2">{{ $t('steps_ImportMembers.validate') }}</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="3">{{ $t('steps_ImportMembers.confirm') }}</v-stepper-step>
        </v-stepper-header>

        <v-stepper-items class="mt-n5">
          <v-stepper-content step="1">
            <v-form v-model="valid">
              <v-container fluid>
                <v-row>
                  <v-file-input :label="$t('firstStep_ImportMembers.fileLabel')" prepend-icon=""
                                :placeholder="$t('firstStep_ImportMembers.fileHint')"
                                :hint="$t('firstStep_ImportMembers.fileHint')" v-model="file" outlined
                                type="file"
                                accept=".xlsx,.xls,.csv" :rules="fileRule">
                    <template v-slot:selection="{ index, text }">
                      <v-chip v-if="index < 2" color="green darken-3" label small>{{ text }}</v-chip>
                    </template>
                  </v-file-input>
                </v-row>
                <v-row>
                  <p>{{ $t('firstStep_ImportMembers.languageDescription') }}</p>
                </v-row>
                <v-row>
                  <v-select :items="languages" v-model="languageKey" item-text="text" item-value="lang" :label="$t('firstStep_ImportMembers.languageLabel')" outlined/>
                </v-row>
                <v-row class="mt-n5">
                  <v-checkbox v-model="containsHeader" :label="$t('firstStep_ImportMembers.containsHeaderLabel')" outlined/>
                </v-row>
                <v-row align="center" justify="center">
                  <v-btn @click="nextStep" :disabled="!valid">{{ $t('btnNext_ImportMembers') }}</v-btn>
                </v-row>
              </v-container>
            </v-form>
          </v-stepper-content>
          <v-stepper-content step="2">
            <v-container fluid>
              <v-row class="d-flex align-center justify-center">
                <p v-html="$t('secondStep_ImportMembers.description')"/>
              </v-row>
              <v-row>
                <v-simple-table height="320px" style="width: 100%" :dense="true" :fixed-header="true">
                  <template v-slot:default>
                    <thead>
                    <tr>
                      <th v-for="item in headers" :key="item" class="text-left">{{ item }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in membersEmails" :key="item">
                      <td>{{ item }}</td>
                    </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-row>
              <v-row align="center" justify="center" class="mt-5 mb-1">
                <v-btn @click="backStep" class="mr-3">{{ $t('btnBack_ImportMembers') }}</v-btn>
                <v-btn @click="nextStep">{{ $t('btnNext_ImportMembers') }}</v-btn>
              </v-row>
            </v-container>
          </v-stepper-content>
          <v-stepper-content step="3">
            <v-container fluid>
              <v-row class="d-flex align-center justify-center">
                <v-img class="mb-5"
                       src="https://hub.swiset.com/wp-content/uploads/2020/06/Import-Trades.svg"
                       max-width="91px" contain/>
              </v-row>
              <v-row class="d-flex align-center justify-center text-center">
                <p>
                  {{ $t('thirdStep_ImportMembers.firstMessage', { count: this.membersEmails.length }) }}.<br>
                  <b>- {{ $t('thirdStep_ImportMembers.alertMessage') }} -</b>
                </p>
              </v-row>
              <v-row class="d-flex align-center justify-center text-center mt-5 mb-1">
                <v-btn class="mr-3" @click="backStep">{{ $t('btnBack_ImportMembers') }}</v-btn>
                <v-btn @click="importMembers">{{ $t('btnImport') }}</v-btn>
              </v-row>
              <p style="font-size: 14px;" class="text-center mb-2 mt-7">
                {{ $t('permissionsDisclaimerOne') }}<br>{{ $t('permissionsDisclaimerTwo') }}
              </p>
            </v-container>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card-text>
  </v-card>
</template>

<script>
import * as XLSX from 'xlsx';
import Repository from "../../../repositories/club";
import { mapMutations } from 'vuex';

export default {
  name: "ImportMembers",
  data() {
    return {
      overlayImporting: false,
      currentStep: 1,

      // Step 1: Choose File
      file: null,
      fullExcel: null,
      languageKey: this.$i18n.locale,
      languages: [
        { text: 'English 🇬🇧', lang: 'en' },
        { text: 'Español 🇪🇸', lang: 'es' },
      ],
      containsHeader: false,

      // Step 2: Validate Data
      membersEmails: [],
      headers: [
        this.$t('secondStep_ImportMembers.emailLabel')
      ],

      // Validations
      valid: false,
      fileRule: [
        f => !!f || 'File is required',
      ],
    };
  },
  methods: {
    backStep() {
      this.currentStep--;
    },
    nextStep() {
      if(this.valid && this.currentStep === 1)
        this.importFile(this.file, 0);
      this.currentStep++;
    },
    importFile(evt, sheetNumber) {
      let f = evt;
      if (f) {
        let r = new FileReader();
        r.onload = e => {
          let contents = this.processExcel(e.target.result);
          this.fullExcel = JSON.parse(contents);
          let dataFull = this.fullExcel[Object.keys(this.fullExcel)[sheetNumber]];
          if(this.containsHeader)
            dataFull.shift();
          this.membersEmails = dataFull;
          this.processMembers();
        };
        r.readAsBinaryString(f);
      }
    },
    processExcel(data) {
      let workbook = XLSX.read(data, {
        type: 'binary'
      });

      // eslint-disable-next-line no-unused-vars
      let firstSheet = workbook.SheetNames[0];
      return this.toJson(workbook);
    },
    toJson(workbook) {
      let result = {};
      workbook.SheetNames.forEach(function(sheetName) {
        let roa = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1,
          raw: false,
        });
        if (roa.length) result[sheetName] = roa;
      });
      return JSON.stringify(result, 2, 2);
    },
    processMembers() {
      let partialList = [];
      this.membersEmails.forEach((member) => partialList.push(member[0]));
      this.membersEmails = partialList;
    },
    ...mapMutations(['setClubMembers']),
    importMembers() {
      this.overlayImporting = true;

      let importingData = {
        clubId: this.$store.state.clubMember.club.id,
        languageKey: this.languageKey,
        peopleList: this.membersEmails,
        //clubBatchId: 15,
      };

      Repository.bulkImportingOfMembers(importingData).then((response) => {
        if(response.status < 400) {
          let summary = response.data;
          Repository.getAllClubMembers(this.$store.state.clubMember.club.id).then((secondResponse) => { if (secondResponse.status < 400) this.setClubMembers(secondResponse.data); });
          alert(this.$t('successfulMessage',
              { addedAsMembersCount: summary.addedAsMembersCount,
                invitationSentCount: summary.invitationSentCount,
                alreadyMembersCount: summary.alreadyMembersCount,
              }));
          this.overlayImporting = false;
          this.close();
        }
      });
    },
    close() {
      this.$emit('closeWindow');
    },
  },
}
</script>



<style scoped>

</style>
